// Story 11.1: Odometer — slot-machine digit-roll for numeric displays.
// Reuses the shared reduced-motion snapshot and lazy GSAP loader from
// `lib/gsap-setup.ts` (architecture-patterns.md Pattern 5). Renders digit
// columns of stacked 0–9 strips and translates each column to the target
// digit with a right-first stagger.

import { useEffect, useMemo, useRef } from 'react';
import { loadGsap, prefersReducedMotion } from '../../../lib/gsap-setup.js';

export interface OdometerProps {
  value: number;
  minDigitCount?: number;
  staggerSeconds?: number;
  durationSeconds?: number;
  className?: string;
  'aria-label'?: string;
}

const DEFAULT_STAGGER = 0.04;
const DEFAULT_DURATION = 0.6;
const DEFAULT_MIN_DIGITS = 1;
const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

function toDigitString(value: number, minDigitCount: number): string {
  const normalized = Math.max(0, Math.floor(Number.isFinite(value) ? value : 0));
  return String(normalized).padStart(Math.max(1, minDigitCount), '0');
}

export function Odometer({
  value,
  minDigitCount = DEFAULT_MIN_DIGITS,
  staggerSeconds = DEFAULT_STAGGER,
  durationSeconds = DEFAULT_DURATION,
  className,
  'aria-label': ariaLabel,
}: OdometerProps) {
  const rootClassName = ['inline-flex font-mono tabular-nums leading-none', className]
    .filter(Boolean)
    .join(' ');

  const digitString = toDigitString(value, minDigitCount);

  if (prefersReducedMotion) {
    return (
      <span className={rootClassName} aria-label={ariaLabel}>
        {digitString}
      </span>
    );
  }

  return (
    <OdometerMotion
      value={value}
      digitString={digitString}
      staggerSeconds={staggerSeconds}
      durationSeconds={durationSeconds}
      rootClassName={rootClassName}
      ariaLabel={ariaLabel}
    />
  );
}

interface OdometerMotionProps {
  value: number;
  digitString: string;
  staggerSeconds: number;
  durationSeconds: number;
  rootClassName: string;
  ariaLabel: string | undefined;
}

function OdometerMotion({
  value,
  digitString,
  staggerSeconds,
  durationSeconds,
  rootClassName,
  ariaLabel,
}: OdometerMotionProps) {
  const rootRef = useRef<HTMLSpanElement>(null);
  const columnRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const previousDigitsRef = useRef<string>('');
  const hasMountedRef = useRef<boolean>(false);

  const digits = useMemo(() => digitString.split(''), [digitString]);

  // Align previous digits to the new width: padStart when growing, slice-from-
  // end when shrinking. Keeps the units column stable across digit-count
  // changes so a 99→100 roll animates the new hundreds column from 0→1 while
  // tens and ones remain aligned to their real previous values.
  const alignPrevious = (previous: string, length: number): string =>
    previous.length >= length ? previous.slice(-length) : previous.padStart(length, '0');

  // Pre-compute each column's initial translateY synchronously so it paints
  // at the previous digit before GSAP loads — removes the one-frame "0" flash.
  const paddedPreviousAtRender = alignPrevious(previousDigitsRef.current, digits.length);

  useEffect(() => {
    columnRefs.current = columnRefs.current.slice(0, digits.length);
  }, [digits.length]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const previousDigits = previousDigitsRef.current;
    const paddedPrevious = alignPrevious(previousDigits, digits.length);
    const isFirstRun = !hasMountedRef.current;

    let cancelled = false;
    let ctx: { revert: () => void } | undefined;

    void loadGsap().then((gsap) => {
      if (cancelled) return;
      ctx = gsap.context(() => {
        digits.forEach((digitChar, columnIndex) => {
          const column = columnRefs.current[columnIndex];
          if (!column) return;
          const targetDigit = Number(digitChar);
          const previousDigit = Number(paddedPrevious[columnIndex] ?? '0');
          const columnOffsetFromRight = digits.length - 1 - columnIndex;
          const delay = staggerSeconds * columnOffsetFromRight;

          gsap.set(column, { y: `-${String(previousDigit)}em` });
          // On mount, every visible column rolls in per AC2 (slot-machine feel,
          // "numbers feel alive"). On updates, skip columns that didn't change.
          if (!isFirstRun && previousDigit === targetDigit) return;

          gsap.to(column, {
            y: `-${String(targetDigit)}em`,
            duration: durationSeconds,
            ease: 'power2.out',
            delay,
          });
        });
      }, root);
      // Record committed state only after GSAP has applied it; if the effect
      // is cancelled before this line runs, the next effect still reads the
      // last visually-committed digit string instead of a cancelled target.
      previousDigitsRef.current = digits.join('');
      hasMountedRef.current = true;
    });

    return () => {
      cancelled = true;
      ctx?.revert();
    };
  }, [value, digits, staggerSeconds, durationSeconds]);

  return (
    <span ref={rootRef} className={rootClassName} aria-label={ariaLabel}>
      <span className="sr-only">{digitString}</span>
      {digits.map((digitChar, columnIndex) => {
        const previousDigit = Number(paddedPreviousAtRender[columnIndex] ?? '0');
        // Key by position-from-right so React preserves the units column (and
        // subsequent lower-order columns) across digit-count changes and only
        // mounts/unmounts the leading column when the number crosses a
        // power-of-10 boundary.
        const positionFromRight = digits.length - 1 - columnIndex;
        return (
          <span
            key={`col-from-right-${String(positionFromRight)}`}
            className="relative inline-block overflow-hidden"
            style={{ height: '1em', width: '0.6em' }}
            aria-hidden="true"
          >
            <span
              ref={(el) => {
                columnRefs.current[columnIndex] = el;
              }}
              className="absolute inset-x-0 top-0 flex flex-col"
              style={{
                willChange: 'transform',
                transform: `translateY(-${String(previousDigit)}em)`,
              }}
              data-digit={digitChar}
            >
              {DIGITS.map((d) => (
                <span key={d} className="block text-center" style={{ height: '1em' }}>
                  {d}
                </span>
              ))}
            </span>
          </span>
        );
      })}
    </span>
  );
}
